import {
  BatchItem,
  BatchJob,
  fetchAmazonProduct,
  flattenAmazonProduct,
  isGenuineRedirect
} from '../CommerceScraperUtils';
import { AuditedProduct, RawScraperRow } from './scoring-engine-constants';
import { auditProductListing } from './scoring-engine-utils';

const CONCURRENCY = 4;

// flattenAmazonProduct() already names most fields identically to RawScraperRow —
// only the ASIN and rating keys differ. Mapped explicitly (rather than spreading a
// cast Record<string, unknown>) so a shape mismatch surfaces as a type error here
// instead of silently flowing through as `unknown`.
const asStringOrNumber = (v: unknown): string | number | undefined => {
  if (typeof v === 'string' || typeof v === 'number') return v;
  return undefined;
};
const asStringOrBoolean = (v: unknown): string | boolean | undefined => {
  if (typeof v === 'string' || typeof v === 'boolean') return v;
  return undefined;
};

const toRawScraperRow = (flattened: Record<string, unknown>): RawScraperRow => ({
  info_asin: String(flattened.asin ?? ''),
  brand: asStringOrNumber(flattened.brand) as string | undefined,
  brand_url: asStringOrNumber(flattened.brand_url) as string | undefined,
  product_category: asStringOrNumber(flattened.product_category) as string | undefined,
  product_group: asStringOrNumber(flattened.product_group) as string | undefined,
  Rating: asStringOrNumber(flattened.rating),
  total_reviews: asStringOrNumber(flattened.total_reviews),
  pASIN: asStringOrNumber(flattened.pASIN) as string | undefined,
  variant_asins: asStringOrNumber(flattened.variant_asins) as string | undefined,
  name: asStringOrNumber(flattened.name) as string | undefined,
  feature_bullets_count: asStringOrNumber(flattened.feature_bullets_count),
  feature_bullets: asStringOrNumber(flattened.feature_bullets) as string | undefined,
  full_description: asStringOrNumber(flattened.full_description) as string | undefined,
  count_of_images: asStringOrNumber(flattened.count_of_images),
  URI: asStringOrNumber(flattened.URI) as string | undefined,
  aplus_present: asStringOrBoolean(flattened.aplus_present),
  climate_pledge_friendly: asStringOrBoolean(flattened.climate_pledge_friendly),
  amazons_choice_flag: asStringOrBoolean(flattened.amazons_choice_flag),
  best_seller_flag: asStringOrBoolean(flattened.best_seller_flag),
  prime_flag: asStringOrBoolean(flattened.prime_flag),
  videos_count: asStringOrNumber(flattened.videos_count),
  video: asStringOrNumber(flattened.video) as string | undefined
});

/**
 * Batch-scores a list of ASINs by fetching each live via the existing scraper
 * infrastructure, flattening the response into the scorecard's raw-row shape,
 * then running it through the scoring engine. Progress is reported via a
 * BatchJob-shaped object so callers can reuse the page's existing
 * <BatchProgress> component.
 */
export const runAsinBatchScore = async (
  asins: string[],
  tld: string,
  onProgress: (job: BatchJob) => void
): Promise<AuditedProduct[]> => {
  const items: BatchItem[] = asins.map(asin => ({ label: asin, status: 'pending' }));
  const job: BatchJob = {
    id: 'local',
    type: 'scorecard',
    platform: 'amazon',
    status: 'running',
    total: asins.length,
    completed: 0,
    items
  };
  onProgress({ ...job, items: [...job.items] });

  const scored: (AuditedProduct | null)[] = new Array(asins.length).fill(null);

  const processIndex = async (index: number): Promise<void> => {
    items[index] = { ...items[index], status: 'running' };
    onProgress({ ...job, items: [...items] });
    try {
      const raw = await fetchAmazonProduct(asins[index], tld);
      // DSH-32549 review feedback (C1, critical): fetchAmazonProduct resolves (doesn't throw) on
      // a redirect, since a redirect carries real product data worth showing on the single Product
      // tab — but here that data belongs to a sibling product, not asins[index]. Scoring it would
      // silently audit the wrong listing under the requested ASIN. processIndex has no separate
      // "redirected" bucket, so this reuses the existing failed-with-message path rather than
      // scoring it as if it were this ASIN's own listing.
      //
      // Uses the shared isGenuineRedirect (not a bare truthy check on raw.redirect_asin) so this
      // agrees with classifyAmazonScrapeStatus on what counts as a real redirect — a bare check
      // would wrongly fail a normal, complete scrape that happened to carry a whitespace-only or
      // self-referential redirect_asin (review feedback follow-up).
      if (isGenuineRedirect(raw)) {
        throw new Error(
          `No independent Amazon listing — redirected to ${raw.redirect_asin}, not scored`
        );
      }
      const flattened = flattenAmazonProduct(raw);
      scored[index] = auditProductListing(
        toRawScraperRow(flattened),
        flattened.asin ? String(flattened.asin) : `asin-${index}`
      );
      items[index] = { ...items[index], status: 'done' };
    } catch (e) {
      items[index] = {
        ...items[index],
        status: 'failed',
        error: e.message
      };
    }
    job.completed += 1;
    onProgress({ ...job, items: [...items] });
  };

  let cursor = 0;
  const worker = async (): Promise<void> => {
    while (cursor < asins.length) {
      const index = cursor++;
      await processIndex(index);
    }
  };
  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, asins.length) }, worker));

  onProgress({ ...job, status: 'complete', items: [...items] });
  return scored.filter((p): p is AuditedProduct => p !== null);
};
